<template>
  <div class="settings-block">
    <div class="settings-block__title">
      <div class="settings-block__title-text">{{ t('Features') }}</div>
      <ui-input
        v-model="search"
        class="search"
        hideDetails="1"
        :placeholder="t('Search')"
        prepend-inner-icon="fas fa-search"
      />
    </div>
    <div>
      <template v-if="!!filteredPlanLimitsKeysGrouping.length">
        <details-subscription-limits-block
          :block="block"
          :search="search"
          v-for="block in filteredPlanLimitsKeysGrouping"
        />
      </template>
      <ui-not-found
        v-if="!filteredPlanLimitsKeysGrouping.length"
        class="justify-content-start mt-16"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue';
import { debounce } from 'lodash';
import { useI18n, usePlanLimits, type PlanLimitsKeyLabelsShort } from '@/composables';
import DetailsSubscriptionLimitsBlock from '@/views/settings/subscription/forms/details-subscription-limits/details-subscription-limits-block.vue';

type PlanLimitsGroup = { name: string; nameHelp?: string; keys: string[] };

const { t } = useI18n();
const { planLimitsKeysGrouping, planLimitsKeyTextShort } = usePlanLimits(undefined);

const search = ref('');
const filteredPlanLimitsKeysGrouping = ref<PlanLimitsGroup[]>([]);

const sortPlanLimitsGrouping = (grouping: PlanLimitsGroup[]) => {
  const labels: PlanLimitsKeyLabelsShort = planLimitsKeyTextShort.value;
  return grouping.map(group => ({
    ...group,
    keys: [...group.keys].sort((firstKey, secondKey) =>
      (labels[firstKey as keyof BillingLimits] ?? '').localeCompare(
        labels[secondKey as keyof BillingLimits] ?? '',
        undefined,
        { sensitivity: 'base' },
      ),
    ),
  }));
};

const filterPlanLimitsGrouping = () => {
  const query = search.value.toLowerCase();
  const labels: PlanLimitsKeyLabelsShort = planLimitsKeyTextShort.value;
  const filtered = planLimitsKeysGrouping.value.reduce(
    (acc: PlanLimitsGroup[], group) => {
      const filteredKeys = group.keys.filter(key => {
        const label = labels[key as keyof BillingLimits];
        return (label ?? '').toLowerCase().includes(query);
      });
      const isFoundInName = group.name.toLowerCase().includes(query);

      if (filteredKeys.length || isFoundInName) {
        acc.push({
          ...group,
          keys: isFoundInName ? group.keys : filteredKeys,
        });
      }

      return acc;
    },
    [],
  );
  filteredPlanLimitsKeysGrouping.value = sortPlanLimitsGrouping(filtered);
};

const debouncedFilter = debounce(filterPlanLimitsGrouping, 500);

watch(
  () => [planLimitsKeysGrouping.value, planLimitsKeyTextShort.value],
  () => {
    filterPlanLimitsGrouping();
  },
  { immediate: true },
);

watch(search, () => {
  debouncedFilter();
});

onBeforeUnmount(() => {
  debouncedFilter.cancel();
});
</script>

<style lang="scss" scoped>
.search {
  width: 200px;
}
</style>
